import React, { forwardRef } from 'react';
import cx from 'classnames';
import * as styles from './Typography.module.css';

export const pxToRem = (px: number): string => `${px / 16}rem`;

type TypographyVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'subtitle'
  | 'label'
  | 'label2'
  | 'tooltip'
  | 'caption'
  | 'subCaption'
  | 'overline';

const tagMap: Record<TypographyVariant, string> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  subtitle: 'h6',
  label: 'label',
  label2: 'label',
  tooltip: 'span',
  caption: 'span',
  subCaption: 'span',
  overline: 'span',
};

const alignMap: Record<string, string> = {
  left: styles.alignLeft,
  center: styles.alignCenter,
  right: styles.alignRight,
  justify: styles.alignJustify,
};

const weightMap: Record<string, string> = {
  light: styles.weightLight,
  regular: styles.weightRegular,
  medium: styles.weightMedium,
  bold: styles.weightBold,
};

const transformMap: Record<string, string> = {
  uppercase: styles.uppercase,
  lowercase: styles.lowercase,
  capitalize: styles.capitalize,
  none: styles.transformNone,
};

interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  variant: TypographyVariant;
  paragraph?: boolean;
  color?: string;
  align?: 'left' | 'center' | 'right' | 'justify';
  fontWeight?: 'light' | 'regular' | 'medium' | 'bold';
  transform?: 'capitalize' | 'lowercase' | 'uppercase' | 'none';
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

const Typography = forwardRef<HTMLElement, TypographyProps>(
  (
    {
      variant,
      paragraph = false,
      color = undefined,
      align = undefined,
      fontWeight = undefined,
      transform = undefined,
      children,
      className = undefined,
      style = undefined,
      ...rest
    },
    ref,
  ) => {
    const Tag = (
      paragraph ? 'p' : tagMap[variant] || 'span'
    ) as React.ElementType;

    return (
      <Tag
        ref={ref as any}
        className={cx(
          styles.typography,
          styles[variant],
          align && alignMap[align],
          fontWeight && weightMap[fontWeight],
          transform && transformMap[transform],
          className,
        )}
        style={color ? { color, ...style } : style}
        {...(rest as any)}
      >
        {children}
      </Tag>
    );
  },
);

Typography.displayName = 'Typography';

export default Typography;
